import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./promotions.css";
import {
  fetchHotels,
  fetchPromotions,
  createPromotion,
  updatePromotion,
  deletePromotion,
} from "./promotionsApi";

// Promotions admin: per-hotel discount rules. Opened from the "Promotions" card on the home page (route /promotions).
//
// Ported from the standalone promotions project (AdminPromotions.jsx). Same fields and table; the differences are that it
// uses its own scoped stylesheet (promotions.css) instead of Tailwind, signs in with the admin password alone (the password
// is checked by the server on every request, see backend/promotionsRoutes.js), and has a Back and a Lock button.
//
// The password is only held in this page's memory: leaving the page or pressing Lock forgets it.

// Blank form matching the condition schema: Promotion Name, Stay Dates, Book Dates, Stay & Book Date Difference (as a
// min/max range), Length of Stay (as a min/max range), and No. of Adults, plus two discount percentages (one for logged-out
// guests, one for members/logged-in) and an active toggle.
const BLANK_FORM = {
  name: "",
  active: true,
  discountPercentGuest: "",
  discountPercentMember: "",
  stayFrom: "",
  stayTo: "",
  bookFrom: "",
  bookTo: "",
  minAdvanceDays: "",
  maxAdvanceDays: "",
  minNights: "",
  maxNights: "",
  minAdults: "",
};

function toFormFromPromo(p) {
  return {
    name: p.name ?? "",
    active: p.active ?? true,
    discountPercentGuest: p.discountPercentGuest ?? "",
    discountPercentMember: p.discountPercentMember ?? "",
    stayFrom: p.stayFrom ?? "",
    stayTo: p.stayTo ?? "",
    bookFrom: p.bookFrom ?? "",
    bookTo: p.bookTo ?? "",
    minAdvanceDays: p.minAdvanceDays ?? "",
    maxAdvanceDays: p.maxAdvanceDays ?? "",
    minNights: p.minNights ?? "",
    maxNights: p.maxNights ?? "",
    minAdults: p.minAdults ?? "",
  };
}

// Turns "" back into null for optional numeric/date fields before sending to the backend, so an empty field really means
// "no restriction" rather than the string "".
function formToPayload(form) {
  const blankToNull = (v) => (v === "" ? null : v);
  const blankToNullNum = (v) => (v === "" ? null : Number(v));
  return {
    name: form.name,
    active: form.active,
    discountPercentGuest: Number(form.discountPercentGuest) || 0,
    discountPercentMember: Number(form.discountPercentMember) || 0,
    stayFrom: blankToNull(form.stayFrom),
    stayTo: blankToNull(form.stayTo),
    bookFrom: blankToNull(form.bookFrom),
    bookTo: blankToNull(form.bookTo),
    minAdvanceDays: blankToNullNum(form.minAdvanceDays),
    maxAdvanceDays: blankToNullNum(form.maxAdvanceDays),
    minNights: blankToNullNum(form.minNights),
    maxNights: blankToNullNum(form.maxNights),
    minAdults: blankToNullNum(form.minAdults),
  };
}

function Field({ label, span2, children }) {
  return (
    <div className={span2 ? "pa-span2" : undefined}>
      <label className="pa-label">{label}</label>
      {children}
    </div>
  );
}

export default function PromotionsAdmin() {
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [hotels, setHotels] = useState([]);
  const [slug, setSlug] = useState(null);
  const [promotions, setPromotions] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(false);

  const [editingId, setEditingId] = useState(null); // null = not editing; "new" = creating
  const [form, setForm] = useState(BLANK_FORM);
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);

  // Only the newest load may update the screen (switching hotel quickly must not show the previous hotel's list).
  const latestLoad = useRef(0);

  const loadPromotions = useCallback(() => {
    if (!authed || !slug) return;
    const mine = ++latestLoad.current;
    setLoading(true);
    setLoadError("");
    fetchPromotions(slug, password)
      .then((list) => {
        if (mine === latestLoad.current) setPromotions(list);
      })
      .catch((e) => {
        if (mine === latestLoad.current) {
          setPromotions([]);
          setLoadError(e.message);
        }
      })
      .finally(() => {
        if (mine === latestLoad.current) setLoading(false);
      });
  }, [authed, slug, password]);

  useEffect(() => {
    loadPromotions();
  }, [loadPromotions]);

  function handleLogin(e) {
    e.preventDefault();
    setAuthError("");
    setLoggingIn(true);
    fetchHotels(password)
      .then((list) => {
        setHotels(list);
        setSlug(list.length > 0 ? list[0].slug : null);
        setAuthed(true);
      })
      .catch((err) => setAuthError(err.message))
      .finally(() => setLoggingIn(false));
  }

  function handleLock() {
    latestLoad.current += 1;
    setPassword("");
    setAuthed(false);
    setAuthError("");
    setHotels([]);
    setSlug(null);
    setPromotions([]);
    setEditingId(null);
    setLoadError("");
    setSaveError("");
  }

  function startCreate() {
    setForm(BLANK_FORM);
    setEditingId("new");
    setSaveError("");
  }
  function startEdit(promo) {
    setForm(toFormFromPromo(promo));
    setEditingId(promo.id);
    setSaveError("");
  }
  function cancelEdit() {
    setEditingId(null);
    setSaveError("");
  }

  async function handleSave(e) {
    e.preventDefault();
    if (saving) return;
    setSaveError("");
    setSaving(true);
    try {
      const payload = formToPayload(form);
      if (editingId === "new") {
        await createPromotion(slug, password, payload);
      } else {
        await updatePromotion(slug, password, editingId, payload);
      }
      setEditingId(null);
      loadPromotions();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this promotion?")) return;
    try {
      await deletePromotion(slug, password, id);
      loadPromotions();
    } catch (err) {
      setLoadError(err.message);
    }
  }

  const setField = (name) => (e) => setForm({ ...form, [name]: e.target.value });

  if (!authed) {
    return (
      <div className="pa-root pa-center">
        <form onSubmit={handleLogin} className="pa-card pa-login">
          <button type="button" className="pa-back" onClick={() => navigate("/HomePage")}>
            ← Back to home
          </button>
          <h1 className="pa-h1 pa-h1-sm">Admin — promotions</h1>
          <p className="pa-sub">Not a public page. Password required to manage discounts.</p>
          <label className="pa-label pa-label-login" htmlFor="pa-password">
            Admin password
          </label>
          <input
            id="pa-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="pa-input pa-mb"
            autoComplete="off"
            autoFocus
          />
          {authError && <p className="pa-error-text">{authError}</p>}
          <button type="submit" className="pa-btn pa-btn-block" disabled={loggingIn || password === ""}>
            {loggingIn ? "Checking…" : "View promotions"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="pa-root pa-page">
      <div className="pa-wrap">
        <div className="pa-topbar">
          <button type="button" className="pa-back" onClick={() => navigate("/HomePage")}>
            ← Back to home
          </button>
          <button type="button" className="pa-link" onClick={handleLock} title="Forget the password on this page">
            Lock
          </button>
        </div>
        <h1 className="pa-h1">Admin — promotions</h1>
        <p className="pa-note">
          These discounts are ours to define — Stayflexi's API doesn't expose promotions, so these rules live entirely
          here and apply on top of Stayflexi's live rates.
        </p>

        <div className="pa-toolbar">
          <label className="pa-label pa-inline-label" htmlFor="pa-hotel">
            Property:
          </label>
          <select
            id="pa-hotel"
            value={slug || ""}
            onChange={(e) => {
              setSlug(e.target.value);
              setEditingId(null);
            }}
            className="pa-select"
          >
            {hotels.map((h) => (
              <option key={h.slug} value={h.slug}>
                {h.name} (ID: {h.hotelId})
              </option>
            ))}
          </select>
          <button type="button" onClick={startCreate} className="pa-btn pa-btn-sm pa-push" disabled={!slug}>
            + New promotion
          </button>
        </div>

        {loadError && <div className="pa-error-box">{loadError}</div>}

        {editingId && (
          <form onSubmit={handleSave} className="pa-card pa-mb">
            <h2 className="pa-h2">{editingId === "new" ? "New promotion" : "Edit promotion"}</h2>
            <div className="pa-grid">
              <Field label="Promotion Name" span2>
                <input
                  type="text"
                  value={form.name}
                  onChange={setField("name")}
                  className="pa-input"
                  placeholder="Diwali Sale"
                  maxLength={120}
                  required
                />
              </Field>

              <Field label="Discount for guests (not logged in) — %">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.discountPercentGuest}
                  onChange={setField("discountPercentGuest")}
                  className="pa-input"
                  placeholder="e.g. 30"
                  required
                />
              </Field>
              <Field label="Discount for members (logged in) — %">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.discountPercentMember}
                  onChange={setField("discountPercentMember")}
                  className="pa-input"
                  placeholder="e.g. 40"
                  required
                />
              </Field>

              <Field label="Stay Dates — from">
                <input type="date" value={form.stayFrom} onChange={setField("stayFrom")} className="pa-input" />
              </Field>
              <Field label="Stay Dates — to">
                <input type="date" value={form.stayTo} onChange={setField("stayTo")} className="pa-input" />
              </Field>

              <Field label="Book Dates — from">
                <input type="date" value={form.bookFrom} onChange={setField("bookFrom")} className="pa-input" />
              </Field>
              <Field label="Book Dates — to">
                <input type="date" value={form.bookTo} onChange={setField("bookTo")} className="pa-input" />
              </Field>

              <Field label="Stay & Book Date Difference — min days advance">
                <input
                  type="number"
                  min="0"
                  value={form.minAdvanceDays}
                  onChange={setField("minAdvanceDays")}
                  className="pa-input"
                  placeholder="e.g. 10"
                />
              </Field>
              <Field label="Stay & Book Date Difference — max days advance">
                <input
                  type="number"
                  min="0"
                  value={form.maxAdvanceDays}
                  onChange={setField("maxAdvanceDays")}
                  className="pa-input"
                  placeholder="e.g. 60"
                />
              </Field>

              <Field label="Length of Stay — min nights">
                <input
                  type="number"
                  min="0"
                  value={form.minNights}
                  onChange={setField("minNights")}
                  className="pa-input"
                  placeholder="e.g. 2"
                />
              </Field>
              <Field label="Length of Stay — max nights">
                <input
                  type="number"
                  min="0"
                  value={form.maxNights}
                  onChange={setField("maxNights")}
                  className="pa-input"
                  placeholder="e.g. 7"
                />
              </Field>

              <Field label="No. of Adults (min)">
                <input
                  type="number"
                  min="0"
                  value={form.minAdults}
                  onChange={setField("minAdults")}
                  className="pa-input"
                  placeholder="e.g. 3"
                />
              </Field>
              <Field label="Active">
                <label className="pa-label pa-check-label">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  />
                  Promotion is live
                </label>
              </Field>
            </div>

            {saveError && <p className="pa-error-text pa-mt">{saveError}</p>}

            <div className="pa-actions">
              <button type="submit" className="pa-btn pa-btn-sm pa-btn-pad" disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </button>
              <button type="button" onClick={cancelEdit} className="pa-cancel">
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="pa-card pa-tablecard">
          {loading ? (
            <p className="pa-empty">Loading…</p>
          ) : promotions.length === 0 ? (
            <p className="pa-empty">No promotions set up yet for this property.</p>
          ) : (
            <table className="pa-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Guest %</th>
                  <th>Member %</th>
                  <th>Stay dates</th>
                  <th>Book dates</th>
                  <th>Advance days</th>
                  <th>Nights</th>
                  <th>Min adults</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {promotions.map((p) => (
                  <tr key={p.id}>
                    <td className="pa-name">{p.name}</td>
                    <td className="pa-nowrap">{p.discountPercentGuest ?? 0}%</td>
                    <td className="pa-nowrap">{p.discountPercentMember ?? 0}%</td>
                    <td className="pa-nowrap">
                      {p.stayFrom || "—"} → {p.stayTo || "—"}
                    </td>
                    <td className="pa-nowrap">
                      {p.bookFrom || "—"} → {p.bookTo || "—"}
                    </td>
                    <td className="pa-nowrap">
                      {p.minAdvanceDays ?? "—"}–{p.maxAdvanceDays ?? "—"}
                    </td>
                    <td className="pa-nowrap">
                      {p.minNights ?? "—"}–{p.maxNights ?? "—"}
                    </td>
                    <td>{p.minAdults ?? "—"}</td>
                    <td>{p.active ? <span className="pa-active">Active</span> : <span className="pa-paused">Paused</span>}</td>
                    <td className="pa-nowrap">
                      <button type="button" onClick={() => startEdit(p)} className="pa-act pa-act-edit">
                        Edit
                      </button>
                      <button type="button" onClick={() => handleDelete(p.id)} className="pa-act pa-act-delete">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <p className="pa-foot">
          A blank date/number field means that condition isn't restricted — e.g. leaving "Stay Dates" blank applies the
          promotion to bookings for any stay date.
        </p>
      </div>
    </div>
  );
}
